import { fetchPlaceAncestorsById, fetchPlaceByGeoCoords, fetchPlaceById, fetchPlaceByName } from 'actions/PlaceActions'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch } from './reduxHooks'
import { getTypeaheadSuggestions } from 'api/search'
import GeoContext from 'contexts/geoContext'
import debounce from 'debounce-promise'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getPlaceKey } from 'lib/geo/geoUtils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'

export function usePlaceById(placeId = '', disable: boolean = false): App.Place | undefined {
  const place = useAppSelector(state => state.destination.places[placeId])
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!disable && placeId) {
      dispatch(fetchPlaceById(placeId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, disable])

  return disable ? undefined : (place ?? undefined)
}

export function usePlaceByCoords(latitude?: number, longitude?: number, levels?: Array<App.PlaceType>): [App.Place | undefined, boolean] {
  const placeKey = getPlaceKey(latitude, longitude, levels)
  const place = useAppSelector(state => state.destination.coordsToPlace[placeKey])
  const fetching = useAppSelector(state => !!state.destination.placesLoading[placeKey])
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (latitude && longitude) {
      dispatch(fetchPlaceByGeoCoords(latitude, longitude, { levels }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeKey])

  return [place ?? undefined, fetching]
}

export function usePlaceByName(name: string | undefined): App.Place | undefined {
  const place = useAppSelector(state => state.destination.locationToPlace[name ?? ''])
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (name) {
      dispatch(fetchPlaceByName(name))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])
  return place ?? undefined
}

export function usePlacesAncestorsById(placeIds: Array<string>): [Array<Array<string>>, Array<boolean>] {
  const ancestorIdsList = useAppSelector(state => state.destination.placeAncestors ?? EmptyArray)
  const fetching = useAppSelector(state => state.destination.fetchingAncestors)
  const dispatch = useAppDispatch()
  const ancestorsList: Array<Array<string>> = useMemo(() => placeIds.map(id => ancestorIdsList[id] ?? []), [placeIds, ancestorIdsList])
  const fetchingList = useMemo<Array<boolean>>(() => placeIds.map(id => !!fetching[id]), [placeIds, fetching])
  useEffect(() => {
    for (const placeId of placeIds) {
      dispatch(fetchPlaceAncestorsById(placeId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeIds])

  return [ancestorsList, fetchingList]
}

export function usePlaceAncestorsById(placeId = ''): [Array<string>, boolean] {
  const placeIdList = useMemo(() => [placeId], [placeId])
  const [ancestorIdsList, fetchingList] = usePlacesAncestorsById(placeIdList)
  const [firstAncestor, firstFetching] = [ancestorIdsList[0], fetchingList[0]]
  return [firstAncestor, firstFetching]
}

interface PlaceBySearchPhraseOptions {
  placeTypes?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchType>
  vertical?: App.SearchPlaceVertical
  disabled?: boolean
  priority?: App.SearchPlacePriority
  limit?: number
  canViewLuxPlusBenefits?: boolean
}

const EmptyObject = {}
export function usePlacesBySearchPhrase(
  phrase: string,
  options: PlaceBySearchPhraseOptions = EmptyObject,
): [Array<App.SearchItem>, boolean, boolean] {
  const { currentRegionCode } = useContext(GeoContext)
  const [fetching, setFetching] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [results, setResults] = useState<Array<App.SearchItem>>([])
  const optionsRef = useRef<PlaceBySearchPhraseOptions>(options)
  optionsRef.current = options

  const isTypeaheadIIIEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.searchNewTypeaheadIII)

  const getDebouncedPredictions = useMemo(
    () =>
      debounce((searchPhrase: string) => {
        if (searchPhrase.length >= 2) {
          return getTypeaheadSuggestions(searchPhrase, currentRegionCode, {
            types: optionsRef.current.placeTypes,
            searchTypes: optionsRef.current.searchTypes,
            vertical: optionsRef.current.vertical,
            priority: optionsRef.current.priority,
            limit: optionsRef.current.limit,
            canViewLuxPlusBenefits: optionsRef.current.canViewLuxPlusBenefits,
            rollout: isTypeaheadIIIEnabled ? ['typeaheadIIIEnabled'] : undefined,
          })
            .then((searchItems) => {
              setResults(searchItems)
              setError(false)
            })
            .catch(() => setError(true))
            .finally(() => setFetching(false))
        } else {
          // search not long enough, means no results, so return empty result
          setFetching(false)
          return Promise.resolve([])
        }
      }, 200),
    [currentRegionCode, isTypeaheadIIIEnabled],
  )

  useEffect(() => {
    if (!options.disabled) {
      setFetching(true)
      getDebouncedPredictions(phrase)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phrase, options.disabled])

  return [results, fetching, error]
}
